import slugify from '@sindresorhus/slugify';
import { useContext, useEffect } from 'react';

import { ProductPrice } from '@/components/ProductPrice/ProductPrice';
import { Context } from '@/components/Store';
import { ActionTypes } from '@/components/Store.d';
import { UpgradeText } from '@/components/UpgradeText/UpgradeText';
import { VoucherInput } from '@/components/VoucherInput/VoucherInput';
import { VoucherPrompt } from '@/components/VoucherPrompt/VoucherPrompt';
import { VoucherText } from '@/components/VoucherText/VoucherText';
import authentication from '@/lib/authentication';
import { shouldShowUpgradeText, type BannerActionType } from '@/lib/banner';
import { isSupporter } from '@/lib/util';

type Props = {
	bannerAction?: BannerActionType;
	showActiveProductWarning?: boolean;
	showSubscriptionOnHoliday?: boolean;
};

const ProductAttributes = ({ bannerAction, showActiveProductWarning, showSubscriptionOnHoliday }: Props) => {
	const [state, dispatch] = useContext(Context);

	useEffect(() => {
		void (async () => {
			const isLoggedIn = await authentication.userIsLoggedIn();
			dispatch({
				type: ActionTypes.isLoggedIn,
				payload: isLoggedIn
			});
		})().catch(() => {
			// Assume not logged in on error
			dispatch({
				type: ActionTypes.isLoggedIn,
				payload: false
			});
		});
	}, [dispatch]);

	/**
	 * Shows voucher prompt, input, or text based on the available information.
	 * If the user is buying a supporter product, nothing is shown.
	 *
	 * @returns {React.ReactNode | null}
	 */
	function getVoucherElement(): React.ReactNode | null {
		if (isSupporter(state.bundle)) {
			return null;
		}

		if (state.voucher?.code) {
			return <VoucherText />;
		} else if (state.showVoucherInput) {
			return <VoucherInput />;
		} else if (state.currentPage === 'paymentPage') {
			return <VoucherPrompt />;
		} else {
			return null;
		}
	}

	const bundleSlug = state.bundle?.name ? slugify(state.bundle.name) : undefined;

	useEffect(() => {
		if (bundleSlug) {
			(window as { tp?: { experience?: { execute: () => void } } })?.tp?.experience?.execute();
		}
	}, [bundleSlug]);

	return (
		<div className="product-attributes">
			<ProductPrice />
			{getVoucherElement()}
			{shouldShowUpgradeText(bannerAction, showActiveProductWarning, showSubscriptionOnHoliday) ? <UpgradeText /> : null}
			<div className={`pricing-update-hint${bundleSlug ? ` ${bundleSlug}` : ''}`} />
		</div>
	);
};

export { ProductAttributes };
